/**
 * `ReasoningMessage` — one collapsible "thinking" panel for an
 * assistant reasoning part.
 *
 *   - Trigger is an inline row: Brain icon + status text (+ separator +
 *     token count) + chevron. Status text shows "Thinking… Ns" with a
 *     shimmer while the model is still writing, then "Thought for N
 *     seconds" once the part settles. No card chrome, no full-width box.
 *   - Content lives in a left vertical-rule (`pl-4 border-l-2`)
 *     beneath the trigger, indented to read like a quote. No outer
 *     border, no background fill.
 *
 * Open/close behaviour:
 *   - Panel starts CLOSED and never opens on its own. Streaming reasoning
 *     ticks the live seconds counter and token count in the trigger row
 *     without revealing the body.
 *   - The user clicks the chevron to expand or collapse. On expand, the
 *     trigger row is pinned to its click position (`stopScroll` +
 *     `escapedFromLock`) so StickToBottom's smooth resize can't push it
 *     off screen.
 */
import { useEffect, useRef, useState } from "react";
import { BrainIcon, ChevronDownIcon } from "lucide-react";
import { MessageResponse, cn, useStickToBottomContext } from "@flint/ui";
import { estimateDisplayTokenCount } from "./text-metrics.ts";

interface ReasoningMessageProps {
  text: string;
  isStreaming?: boolean;
}

export function ReasoningMessage({ text, isStreaming = false }: ReasoningMessageProps) {
  // Panel starts CLOSED and stays closed unless the user clicks. No
  // auto-open during streaming, no auto-close after — the reasoning is a
  // deliberate opt-in view, not a passing peep-show.
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState<number | undefined>(undefined);
  const [liveSeconds, setLiveSeconds] = useState(0);
  const startRef = useRef<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const stick = useStickToBottomContext();

  const trimmed = text.trim();
  const isRedacted = trimmed === "[REDACTED]";

  // Track streaming duration + tick a live seconds counter every 1 s so the
  // trigger reads "Thinking… 3s". setInterval (not rAF) so the counter keeps
  // advancing even when the tab is backgrounded.
  useEffect(() => {
    if (!isStreaming) {
      if (startRef.current !== null) {
        setDuration(Math.max(1, Math.ceil((Date.now() - startRef.current) / 1000)));
        startRef.current = null;
      }
      return undefined;
    }
    if (startRef.current === null) startRef.current = Date.now();
    setLiveSeconds(Math.max(1, Math.ceil((Date.now() - startRef.current) / 1000)));
    const id = window.setInterval(() => {
      if (startRef.current !== null) {
        setLiveSeconds(Math.max(1, Math.ceil((Date.now() - startRef.current) / 1000)));
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [isStreaming]);

  // Only skip when there's literally nothing to convey. A redacted part
  // still deserves a visible trigger so the user knows reasoning happened.
  if (!isStreaming && trimmed.length === 0) return null;

  return (
    // No my-* here: parent AIMessage owns spacing via gap-4. Adding
    // local margin compounded with the parent gap and made the gap
    // before/after the "Thought for…" panel feel oversized relative
    // to sibling tool-call rows.
    <div className="not-prose flex flex-col">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        onClick={() => {
          const willOpen = !open;
          // Snapshot BEFORE state flips so we can pin the trigger row
          // to its current viewport position after the resize. Without
          // this, StickToBottom's `resize="smooth"` re-anchors the
          // transcript bottom and the newly-expanded reasoning body
          // (plus the trigger the user just clicked) slides off screen.
          const anchorTop = willOpen ? triggerRef.current?.getBoundingClientRect().top : null;
          setOpen(willOpen);
          if (willOpen && anchorTop !== null && anchorTop !== undefined) {
            requestAnimationFrame(() => {
              const scroller = stick?.scrollRef.current;
              const trigger = triggerRef.current;
              if (!scroller || !trigger) return;
              stick?.stopScroll();
              // Also flip StickToBottom into escaped-from-lock so its
              // ResizeObserver callback (fires after this frame) won't
              // re-animate to bottom.
              if (stick?.state) stick.state.escapedFromLock = true;
              const delta = trigger.getBoundingClientRect().top - anchorTop;
              if (delta !== 0) scroller.scrollTop += delta;
            });
          }
        }}
        className="flex w-fit items-center gap-2 text-sm text-muted/80 transition-colors hover:text-ink"
      >
        <BrainIcon className="h-4 w-4 shrink-0" aria-hidden />
        <span className={cn(isStreaming && "ai-chat-shimmer font-medium")}>
          {isStreaming
            ? `Thinking… ${liveSeconds}s`
            : isRedacted
              ? "Reasoned privately"
              : describeDuration(duration)}
        </span>
        {isRedacted ? null : (
          <>
            <span className="text-muted/60" aria-hidden>
              |
            </span>
            <span className="tabular-nums">
              {estimateDisplayTokenCount(trimmed).toLocaleString("en-IN")} tokens
            </span>
          </>
        )}
        <ChevronDownIcon
          className={cn("h-4 w-4 shrink-0 transition-transform", open ? "rotate-180" : "rotate-0")}
          aria-hidden
        />
      </button>

      {open ? (
        <div className="mt-2 border-l-2 border-line pl-4 text-xs text-muted/80">
          {isRedacted ? (
            <span className="italic">
              The model&apos;s private reasoning was hidden by the provider.
            </span>
          ) : trimmed ? (
            <MessageResponse
              className={cn(
                // Reasoning-scale typography: 12 px text, 18 px line-height,
                // one shade lighter than the surrounding muted so the block
                // reads as a subordinate transcript beneath the main reply.
                "prose max-w-none",
                "text-xs text-muted/80",
                "[&_p]:text-xs [&_p]:text-muted/80 [&_p]:leading-[18px] [&_p]:my-0",
                "[&_li]:text-xs [&_li]:text-muted/80 [&_li]:leading-[18px] [&_li]:my-0 [&_li]:py-0 [&_li]:pl-0",
                // Nuke list markers + padding so bullet chains and paragraphs
                // start at the same left column — every line reads as a
                // single flat block, no ragged indent from `list-inside`.
                "[&_ul]:my-0 [&_ol]:my-0 [&_ul]:list-none [&_ol]:list-none [&_ul]:pl-0 [&_ol]:pl-0",
                // 2 px between adjacent block siblings — no visible gap.
                "[&>*+*]:mt-0.5",
                // Streamdown wraps some list-item content in a <p> on blank lines;
                // zero that nested margin so bullet chains stay tight.
                "[&_li>p]:my-0 [&_li>p]:leading-[18px]",
                "[&_h1]:text-xs [&_h2]:text-xs [&_h3]:text-xs [&_h4]:text-xs",
                "[&_h1]:text-muted/80 [&_h2]:text-muted/80 [&_h3]:text-muted/80 [&_h4]:text-muted/80",
                "[&_strong]:text-muted/80 [&_strong]:font-medium",
                "[&_em]:text-muted/80 [&_a]:text-muted/80",
                // Inline code chips — matched to the reduced reasoning scale.
                "[&_code]:text-xs [&_code]:text-muted/80 [&_code]:bg-paper-2",
                "[&_code]:font-normal [&_code]:rounded-sm [&_code]:px-1 [&_code]:py-0",
              )}
            >
              {trimmed}
            </MessageResponse>
          ) : (
            <span className="italic">Thinking…</span>
          )}
        </div>
      ) : null}
    </div>
  );
}

function describeDuration(duration: number | undefined): string {
  if (duration === undefined || duration === 0) return "Thought for a few seconds";
  if (duration === 1) return "Thought for 1 second";
  return `Thought for ${duration} seconds`;
}
